import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM } from "@/lib/format";
import { toast } from "sonner";
import {
  Download, Loader2, Play, FlaskConical, Lock, Unlock, ChevronDown, ChevronRight, Percent,
} from "lucide-react";

const CUR_MONTH = new Date().toISOString().slice(0, 7);

function monthOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 24; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

export default function Commission() {
  const [tab, setTab] = useState("report");
  const [month, setMonth] = useState(CUR_MONTH);
  const [report, setReport] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState({});
  const [preview, setPreview] = useState(null);
  const [register, setRegister] = useState([]);

  const loadReport = () => {
    setLoading(true);
    api.get("/commission/report", { params: { month } })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load report"))
      .finally(() => setLoading(false));
  };

  const loadRegister = () => {
    api.get("/commission/bad-debt-register")
      .then((r) => setRegister(r.data.loans || []))
      .catch(() => {});
  };

  useEffect(() => { if (tab === "report") { setPreview(null); loadReport(); } }, [month, tab]);
  useEffect(() => { if (tab === "register") loadRegister(); }, [tab]);

  const runPreview = async () => {
    setBusy("preview");
    try {
      const { data } = await api.post("/commission/preview", null, { params: { month } });
      setPreview(data.runs || []);
      toast.success("Preview generated (dry-run — nothing written).");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally { setBusy(""); }
  };

  const runMonthEnd = async () => {
    if (!window.confirm(`Run month-end for ${month}? This writes bad-debt events and updates loan commission status.`)) return;
    setBusy("run");
    try {
      const { data } = await api.post("/commission/run", null, { params: { month } });
      const d = (data.runs || []).reduce((a, r) => a + r.deducts, 0);
      const rv = (data.runs || []).reduce((a, r) => a + r.reverses, 0);
      toast.success(`Month-end complete: ${d} deduction(s), ${rv} reversal(s).`);
      setPreview(null);
      loadReport();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Run failed");
    } finally { setBusy(""); }
  };

  const toggleLock = async (row) => {
    const lock = row.cycleStatus !== "locked";
    setBusy(`lock-${row.branch}`);
    try {
      await api.post(`/commission/cycles/${lock ? "lock" : "unlock"}`, { branch: row.branch, cycle: month });
      toast.success(`Cycle ${month} ${lock ? "locked" : "unlocked"} for ${row.branch}.`);
      loadReport();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(""); }
  };

  const exportCsv = () => {
    const head = ["Branch", "Manager", "Interest Received", "Bad Debt Count", "Bad Debt Amount", "Reversal Count", "Reversal Amount", "Eligible Revenue", "Rate %", "Commission", "Cycle Status"];
    const lines = report.rows.map((r) => [
      r.branch, r.managerName, r.interestReceived, r.badDebtCount, r.badDebtAmount,
      r.reversalCount, r.reversalAmount, r.eligibleRevenue, r.rate, r.commission, r.cycleStatus,
    ]);
    const t = report.totals || {};
    lines.push(["TOTAL", "", t.interestReceived, "", t.badDebtAmount, "", t.reversalAmount, t.eligibleRevenue, "", t.commission, ""]);
    const csv = [head, ...lines].map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `commission_${month}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const previewByBranch = useMemo(() => {
    const m = {};
    (preview || []).forEach((r) => { m[r.branch] = r; });
    return m;
  }, [preview]);

  return (
    <Layout>
      <PageHeader
        title="Manager Commission"
        subtitle="Per-branch monthly commission with bad-debt deductions & reversals"
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="commission-month-select"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="py-2 pl-3 pr-8 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 focus:ring-2 focus:ring-slate-900 outline-none cursor-pointer"
            >
              {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <button data-testid="commission-preview-btn" onClick={runPreview} disabled={busy === "preview"}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-800 hover:bg-slate-50 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50">
              {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} Preview month-end
            </button>
            <button data-testid="commission-run-btn" onClick={runMonthEnd} disabled={busy === "run"}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50">
              {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run month-end
            </button>
            <button data-testid="commission-export-btn" onClick={exportCsv} disabled={report.rows.length === 0}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-800 hover:bg-slate-50 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50">
              <Download className="h-4 w-4" /> CSV
            </button>
          </div>
        }
      />

      <div className="inline-flex rounded-md border border-slate-300 overflow-hidden mb-4" data-testid="commission-tabs">
        <button data-testid="commission-tab-report" onClick={() => setTab("report")}
          className={`px-4 py-2 text-sm font-medium transition-colors ${tab === "report" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
          Commission Report
        </button>
        <button data-testid="commission-tab-register" onClick={() => setTab("register")}
          className={`px-4 py-2 text-sm font-medium transition-colors ${tab === "register" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
          Bad-Debt Register
        </button>
      </div>

      {preview && tab === "report" && (
        <div data-testid="commission-preview-banner" className="mb-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          <span className="font-semibold">Preview (dry-run)</span> for {month} — showing what a run would do, nothing has been written.
          {(preview || []).map((r) => (
            <span key={r.branch} className="ml-2">[{r.branch}: {r.deducts} deduct, {r.reverses} reverse]</span>
          ))}
        </div>
      )}

      {tab === "report" && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="commission-report-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-3 font-medium">Branch / Manager</th>
                <th className="px-3 py-3 font-medium text-right">Interest Received</th>
                <th className="px-3 py-3 font-medium text-right">New Bad Debt</th>
                <th className="px-3 py-3 font-medium text-right">Reversals</th>
                <th className="px-3 py-3 font-medium text-right">Eligible Revenue</th>
                <th className="px-3 py-3 font-medium text-right">Rate</th>
                <th className="px-3 py-3 font-medium text-right">Commission</th>
                <th className="px-3 py-3 font-medium text-center">Cycle</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => {
                const key = r.branch;
                const isOpen = expanded[key];
                const pv = previewByBranch[r.branch];
                return (
                  <React.Fragment key={key}>
                    <tr className="border-b border-slate-50 hover:bg-slate-50" data-testid={`commission-row-${r.branch}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                          className="inline-flex items-center gap-1.5 font-medium text-slate-900" data-testid={`commission-expand-${r.branch}`}>
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          {r.branch}
                        </button>
                        <div className="text-xs text-slate-400 ml-5">{r.managerName || "— no manager —"}</div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono-fig text-green-600">{fmtRM(r.interestReceived)}</td>
                      <td className="px-3 py-3 text-right font-mono-fig text-red-600" data-testid={`commission-baddebt-${r.branch}`}>
                        {r.badDebtCount > 0 ? `−${fmtRM(r.badDebtAmount)}` : fmtRM(0)}
                        {r.badDebtCount > 0 && <span className="text-slate-400"> ({r.badDebtCount})</span>}
                        {pv && <span className="ml-1 text-[11px] text-amber-600">▲{pv.deducts}</span>}
                      </td>
                      <td className="px-3 py-3 text-right font-mono-fig text-emerald-600">
                        {r.reversalCount > 0 ? `+${fmtRM(r.reversalAmount)}` : fmtRM(0)}
                        {r.reversalCount > 0 && <span className="text-slate-400"> ({r.reversalCount})</span>}
                      </td>
                      <td className="px-3 py-3 text-right font-mono-fig font-semibold text-slate-900">{fmtRM(r.eligibleRevenue)}</td>
                      <td className="px-3 py-3 text-right font-mono-fig text-slate-600">{r.rate}%</td>
                      <td className="px-3 py-3 text-right font-mono-fig font-bold text-slate-900" data-testid={`commission-amount-${r.branch}`}>{fmtRM(r.commission)}</td>
                      <td className="px-3 py-3 text-center">
                        <button data-testid={`commission-lock-${r.branch}`} onClick={() => toggleLock(r)} disabled={busy === `lock-${r.branch}`}
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${r.cycleStatus === "locked" ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
                          {r.cycleStatus === "locked" ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                          {r.cycleStatus === "locked" ? "Locked" : "Open"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/60" data-testid={`commission-detail-${r.branch}`}>
                        <td colSpan={8} className="px-6 py-3">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs font-semibold uppercase text-red-600 mb-1">Bad-debt deductions</div>
                              {r.badDebtLoans.length === 0 && <div className="text-xs text-slate-400">None</div>}
                              {r.badDebtLoans.map((l) => (
                                <div key={l.loanCode} className="flex justify-between text-xs py-0.5">
                                  <span>{l.loanCode} — {l.customerName}</span>
                                  <span className="font-mono-fig text-red-600">−{fmtRM(l.amount)}</span>
                                </div>
                              ))}
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase text-emerald-600 mb-1">Reversals</div>
                              {r.reversalLoans.length === 0 && <div className="text-xs text-slate-400">None</div>}
                              {r.reversalLoans.map((l) => (
                                <div key={l.loanCode} className="flex justify-between text-xs py-0.5">
                                  <span>{l.loanCode} — {l.customerName}</span>
                                  <span className="font-mono-fig text-emerald-600">+{fmtRM(l.amount)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!loading && report.rows.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-400" data-testid="commission-empty">No commission-enabled branches. Turn on the module in Account → Commission Settings.</td></tr>
              )}
              {loading && <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-400 animate-pulse">Loading…</td></tr>}
            </tbody>
            {report.rows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-900 text-white font-semibold">
                  <td className="px-4 py-3">Total</td>
                  <td className="px-3 py-3 text-right font-mono-fig">{fmtRM(report.totals.interestReceived || 0)}</td>
                  <td className="px-3 py-3 text-right font-mono-fig">−{fmtRM(report.totals.badDebtAmount || 0)}</td>
                  <td className="px-3 py-3 text-right font-mono-fig">+{fmtRM(report.totals.reversalAmount || 0)}</td>
                  <td className="px-3 py-3 text-right font-mono-fig">{fmtRM(report.totals.eligibleRevenue || 0)}</td>
                  <td className="px-3 py-3"></td>
                  <td className="px-3 py-3 text-right font-mono-fig" data-testid="commission-total">{fmtRM(report.totals.commission || 0)}</td>
                  <td className="px-3 py-3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {tab === "register" && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="commission-register-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-3 font-medium">Branch</th>
                <th className="px-4 py-3 font-medium">Loan / Customer</th>
                <th className="px-3 py-3 font-medium">Cycle Flagged</th>
                <th className="px-3 py-3 font-medium text-right">Deducted Principal</th>
                <th className="px-3 py-3 font-medium text-right">Overdue Days</th>
                <th className="px-3 py-3 font-medium text-right">Current Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {register.map((l) => (
                <tr key={`${l.branch}-${l.loanCode}`} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`register-row-${l.loanCode}`}>
                  <td className="px-4 py-3 font-medium">{l.branch}</td>
                  <td className="px-4 py-3">{l.loanCode} — <span className="text-slate-500">{l.customerName}</span></td>
                  <td className="px-3 py-3 font-mono-fig text-slate-600">{l.cycleFlagged || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono-fig text-red-600">{l.deductedPrincipal != null ? fmtRM(l.deductedPrincipal) : "—"}</td>
                  <td className="px-3 py-3 text-right font-mono-fig">{l.currentOverdueDays}</td>
                  <td className="px-3 py-3 text-right font-mono-fig">{fmtRM(l.currentOutstandingPrincipal)}</td>
                </tr>
              ))}
              {register.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400" data-testid="register-empty">No loans currently flagged as commission bad-debt.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
